import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaWhatsapp, FaDirections
} from 'react-icons/fa';
import { submitContact } from '../services/api';
import Loader from '../components/Loader';
import Reveal from '../components/Reveal';
import { useT } from '../i18n/LanguageContext';
import { gridContainer, gridItem, heroContainer, fromLeft, fromRight, viewportOnce } from '../components/motionPresets';
import './Contact.css';

// Single source of truth for contact details — replace the placeholders below
// with the real values. WHATSAPP_NUMBER must be digits only, country code
// included, no "+" or spaces (wa.me format), e.g. '237690000000'.
const PHONE_DISPLAY = '+237 696 466 958';
const PHONE_TEL = '+237696466958';
const EMAIL = 'info@elecsolution.com';
const WHATSAPP_DISPLAY = '+237 696 466 958';
const WHATSAPP_NUMBER = '237696466958';

// Branch locations (map shown via keyless Google Maps embed).
const LOCATIONS = [
    { city: 'Douala', area: 'PK12', query: 'PK 12, Douala, Cameroun' },
    { city: 'Yaoundé', area: 'Damas (entrée Terre Rouge)', query: 'Damas, Yaoundé, Cameroun' },
    { city: 'Yaoundé', area: 'Neptune Tamtam', query: 'Neptune, Yaoundé, Cameroun' },
];

const T = {
    en: {
        heroTitle: 'Contact Us',
        heroSubtitle: "We'd love to hear from you. Get in touch today!",
        getInTouch: 'Get In Touch',
        getInTouchText: "Have questions about our services, products, or training programs? Reach out and we'll respond promptly.",
        ourLocations: 'Our Locations',
        locationsValue: 'Yaounde & Douala, Cameroon',
        phone: 'Phone',
        email: 'Email',
        businessHours: 'Business Hours',
        businessHoursValue: 'Mon - Sat: 8:00 AM - 6:00 PM',
        whatsapp: 'WhatsApp',
        whatsappHint: 'Chat with us on WhatsApp',
        sendMessage: 'Send Us a Message',
        fullName: 'Full Name *',
        fullNamePlaceholder: 'Your full name',
        emailLabel: 'Email *',
        emailPlaceholder: 'Your email',
        phoneLabel: 'Phone',
        phonePlaceholder: 'Your phone number',
        subject: 'Subject',
        subjectPlaceholder: 'Message subject',
        message: 'Message *',
        messagePlaceholder: 'How can we help you?',
        sendBtn: 'Send Message',
        sending: 'Sending',
        successMsg: "Message sent successfully! We'll get back to you soon.",
        errorMsg: 'Failed to send message. Please try again.',
        locationsSubtitle: 'Find us in Douala and Yaoundé — drop by any of our branches.',
        getDirections: 'Get directions',
        mapOf: (city, area) => `Map of ${city} - ${area}`,
    },
    fr: {
        heroTitle: 'Contactez-nous',
        heroSubtitle: 'Nous serions ravis de vous lire. Contactez-nous dès aujourd’hui !',
        getInTouch: 'Entrer en contact',
        getInTouchText: 'Vous avez des questions sur nos services, produits ou formations ? Écrivez-nous, nous vous répondrons rapidement.',
        ourLocations: 'Nos emplacements',
        locationsValue: 'Yaoundé & Douala, Cameroun',
        phone: 'Téléphone',
        email: 'E-mail',
        businessHours: 'Heures d’ouverture',
        businessHoursValue: 'Lun - Sam : 8h00 - 18h00',
        whatsapp: 'WhatsApp',
        whatsappHint: 'Discutez avec nous sur WhatsApp',
        sendMessage: 'Envoyez-nous un message',
        fullName: 'Nom complet *',
        fullNamePlaceholder: 'Votre nom complet',
        emailLabel: 'E-mail *',
        emailPlaceholder: 'Votre e-mail',
        phoneLabel: 'Téléphone',
        phonePlaceholder: 'Votre numéro de téléphone',
        subject: 'Sujet',
        subjectPlaceholder: 'Objet du message',
        message: 'Message *',
        messagePlaceholder: 'Comment pouvons-nous vous aider ?',
        sendBtn: 'Envoyer le message',
        sending: 'Envoi',
        successMsg: 'Message envoyé avec succès ! Nous vous répondrons bientôt.',
        errorMsg: 'Échec de l’envoi du message. Veuillez réessayer.',
        locationsSubtitle: 'Retrouvez-nous à Douala et Yaoundé — passez dans l’une de nos agences.',
        getDirections: 'Obtenir l’itinéraire',
        mapOf: (city, area) => `Carte de ${city} - ${area}`,
    },
};

function Contact() {
    const t = useT(T);
    const [formData, setFormData] = useState({
        full_name: '', email: '', phone: '', subject: '', message: ''
    });
    const [status, setStatus] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await submitContact(formData);
            setStatus('success');
            setFormData({ full_name: '', email: '', phone: '', subject: '', message: '' });
        } catch (err) {
            setStatus('error');
        } finally {
            setSubmitting(false);
            setTimeout(() => setStatus(''), 4000);
        }
    };

    return (
        <div className="contact-page">
            <motion.section className="page-hero" initial="hidden" animate="show" variants={heroContainer}>
                <motion.h1 variants={gridItem}>{t.heroTitle}</motion.h1>
                <motion.p variants={gridItem}>{t.heroSubtitle}</motion.p>
            </motion.section>

            <section className="contact-section">
                <div className="contact-container">
                    <motion.div className="contact-info" variants={fromLeft} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        <h2>{t.getInTouch}</h2>
                        <p>{t.getInTouchText}</p>

                        <div className="info-items">
                            <div className="info-item">
                                <FaMapMarkerAlt className="info-icon" />
                                <div>
                                    <h4>{t.ourLocations}</h4>
                                    <p>{t.locationsValue}</p>
                                </div>
                            </div>
                            <a className="info-item info-item-link" href={`tel:${PHONE_TEL}`}>
                                <FaPhone className="info-icon" />
                                <div>
                                    <h4>{t.phone}</h4>
                                    <p>{PHONE_DISPLAY}</p>
                                </div>
                            </a>
                            <a className="info-item info-item-link" href={`mailto:${EMAIL}`}>
                                <FaEnvelope className="info-icon" />
                                <div>
                                    <h4>{t.email}</h4>
                                    <p>{EMAIL}</p>
                                </div>
                            </a>
                            <div className="info-item">
                                <FaClock className="info-icon" />
                                <div>
                                    <h4>{t.businessHours}</h4>
                                    <p>{t.businessHoursValue}</p>
                                </div>
                            </div>
                            <a
                                className="info-item info-item-link"
                                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FaWhatsapp className="info-icon whatsapp" />
                                <div>
                                    <h4>{t.whatsapp}</h4>
                                    <p>{WHATSAPP_DISPLAY}</p>
                                    <span className="info-item-hint">{t.whatsappHint}</span>
                                </div>
                            </a>
                        </div>
                    </motion.div>

                    <motion.div className="contact-form-container" variants={fromRight} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        <h2>{t.sendMessage}</h2>
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t.fullName}</label>
                                    <input
                                        type="text"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleChange}
                                        required
                                        placeholder={t.fullNamePlaceholder}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t.emailLabel}</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        placeholder={t.emailPlaceholder}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t.phoneLabel}</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder={t.phonePlaceholder}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t.subject}</label>
                                    <input
                                        type="text"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        placeholder={t.subjectPlaceholder}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t.message}</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows="5"
                                    placeholder={t.messagePlaceholder}
                                ></textarea>
                            </div>
                            <button type="submit" className="btn btn-primary submit-btn" disabled={submitting}>
                                {submitting ? <Loader inline label={t.sending} /> : t.sendBtn}
                            </button>
                            {status === 'success' && (
                                <p className="form-status success">{t.successMsg}</p>
                            )}
                            {status === 'error' && (
                                <p className="form-status error">{t.errorMsg}</p>
                            )}
                        </form>
                    </motion.div>
                </div>
            </section>

            {/* ===== Locations + maps ===== */}
            <section className="contact-extra locations-extra">
                <div className="extra-container">
                    <Reveal><h2 className="extra-title">{t.ourLocations}</h2></Reveal>
                    <Reveal delay={0.08}><p className="extra-subtitle">{t.locationsSubtitle}</p></Reveal>
                    <motion.div className="loc-grid" variants={gridContainer} initial="hidden" whileInView="show" viewport={viewportOnce}>
                        {LOCATIONS.map((loc, i) => (
                            <motion.div key={i} className="loc-card" variants={gridItem}>
                                <div className="loc-map">
                                    <iframe
                                        title={t.mapOf(loc.city, loc.area)}
                                        src={`https://maps.google.com/maps?q=${encodeURIComponent(loc.query)}&z=14&output=embed`}
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        allowFullScreen
                                    />
                                </div>
                                <div className="loc-info">
                                    <div className="loc-head">
                                        <FaMapMarkerAlt className="loc-pin" />
                                        <div>
                                            <h3>{loc.city}</h3>
                                            <p>{loc.area}</p>
                                        </div>
                                    </div>
                                    <a
                                        className="loc-directions"
                                        href={`https://maps.google.com/maps?q=${encodeURIComponent(loc.query)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FaDirections /> {t.getDirections}
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>
        </div>
    );
}

export default Contact;
